<template>
  <div class="flex h-screen bg-gray-100">
    <!-- Боковая панель -->
    <StaffSidebar />

    <!-- Основной контент -->
    <div class="flex-1 transition-all duration-300 overflow-auto" style="margin-left: var(--sidebar-width, 260px)">
      <div class="py-6 px-8">
        <!-- Заголовок -->
        <div class="flex justify-between items-center mb-6">
          <div>
            <h1 class="text-2xl font-semibold text-gray-800">{{ t('Опросники') }}</h1>
            <p class="text-sm text-gray-500 mt-1">{{ t('Создание и управление опросами для пациентов') }}</p>
          </div>
          <button
            @click="createNewSurvey"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center text-sm font-medium"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            {{ t('Создать опрос') }}
          </button>
        </div>

        <!-- Статистика карточки -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-white rounded-lg shadow p-4">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-2xl font-bold text-gray-800">{{ stats.total }}</p>
                <p class="text-sm text-gray-500">{{ t('Всего опросов') }}</p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow p-4">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-2xl font-bold text-gray-800">{{ stats.active }}</p>
                <p class="text-sm text-gray-500">{{ t('Активных') }}</p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow p-4">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-2xl font-bold text-gray-800">{{ stats.totalResponses }}</p>
                <p class="text-sm text-gray-500">{{ t('Всего ответов') }}</p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow p-4">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-2xl font-bold text-gray-800">{{ stats.draft }}</p>
                <p class="text-sm text-gray-500">{{ t('Черновики') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Фильтры и поиск -->
        <div class="bg-white rounded-lg shadow mb-6">
          <div class="p-4 border-b border-gray-200">
            <div class="flex flex-wrap items-center gap-4">
              <!-- Поиск -->
              <div class="flex-1 min-w-[200px]">
                <div class="relative">
                  <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    v-model="searchQuery"
                    @input="queueReload"
                    type="text"
                    :placeholder="t('Поиск по названию...')"
                    class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <!-- Фильтр по статусу -->
              <select
                v-model="filterStatus"
                @change="loadSurveys(1)"
                class="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{{ t('Все статусы') }}</option>
                <option value="active">{{ t('Активные') }}</option>
                <option value="inactive">{{ t('Неактивные') }}</option>
              </select>

              <!-- Фильтр по языку -->
              <select
                v-model="filterLanguage"
                @change="loadSurveys(1)"
                class="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{{ t('Все языки') }}</option>
                <option value="ru">{{ t('Русский') }}</option>
                <option value="kz">{{ t('Казахский') }}</option>
              </select>

              <!-- Кнопка обновить -->
              <button
                @click="loadSurveys(page)"
                class="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
                :disabled="loading"
              >
                <svg
                  class="w-5 h-5 mr-2"
                  :class="{ 'animate-spin': loading }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {{ t('Обновить') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Состояние загрузки -->
        <div v-if="loading" class="bg-white rounded-lg shadow p-8 text-center">
          <svg class="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-gray-500">{{ t('Загрузка опросов...') }}</p>
        </div>

        <!-- Ошибка -->
        <div v-else-if="error" class="bg-red-50 rounded-lg shadow p-8 text-center">
          <svg class="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-red-600 mb-4">{{ error }}</p>
          <button @click="loadSurveys(page)" class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            {{ t('Повторить') }}
          </button>
        </div>

        <!-- Пустое состояние -->
        <div v-else-if="filteredSurveys.length === 0" class="bg-white rounded-lg shadow p-8 text-center">
          <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ searchQuery || filterStatus || filterLanguage ? t('Опросы не найдены') : t('Нет созданных опросов') }}
          </h3>
          <p class="text-gray-500 mb-4">
            {{ searchQuery || filterStatus || filterLanguage ? t('Попробуйте изменить критерии поиска') : t('Создайте первый опрос для сбора обратной связи') }}
          </p>
          <button
            v-if="!searchQuery && !filterStatus && !filterLanguage"
            @click="createNewSurvey"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {{ t('Создать первый опрос') }}
          </button>
        </div>

        <!-- Список опросов -->
        <div v-else class="space-y-4">
          <div
            v-for="survey in filteredSurveys"
            :key="survey.id"
            class="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div class="p-4">
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-3 mb-2">
                    <h3 class="text-lg font-medium text-gray-900 truncate">{{ survey.name }}</h3>
                    <!-- Бейджи -->
                    <span
                      :class="[
                        'px-2 py-0.5 text-xs font-medium rounded-full',
                        survey.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      ]"
                    >
                      {{ survey.is_active ? t('Активен') : t('Черновик') }}
                    </span>
                    <span class="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      {{ survey.language === 'ru' ? 'RU' : 'KZ' }}
                    </span>
                    <span v-if="survey.is_anonymous" class="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                      {{ t('Анонимный') }}
                    </span>
                  </div>

                  <p v-if="survey.description" class="text-sm text-gray-500 mb-3 line-clamp-2">
                    {{ survey.description }}
                  </p>

                  <div class="flex items-center gap-4 text-sm text-gray-500">
                    <span class="flex items-center">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {{ survey.question_count || 0 }} {{ t('вопросов') }}
                    </span>
                    <span class="flex items-center">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                      </svg>
                      {{ survey.response_count || 0 }} {{ t('ответов') }}
                    </span>
                    <span class="flex items-center">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {{ formatDate(survey.created_at) }}
                    </span>
                    <span v-if="survey.paired_survey_name" class="flex items-center text-blue-600">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      {{ t('Парная') }}: {{ survey.paired_survey_name }}
                    </span>
                  </div>
                </div>

                <!-- Кнопки действий -->
                <div class="flex items-center gap-2 ml-4">
                  <button
                    @click="editSurvey(survey.id)"
                    class="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    :title="t('Изменить')"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>

                  <button
                    @click="viewStatistics(survey.id)"
                    class="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                    title="Статистика"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </button>

                  <button
                    @click="toggleActive(survey)"
                    :class="[
                      'p-2 rounded-md transition-colors',
                      survey.is_active
                        ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50'
                        : 'text-green-500 hover:text-green-600 hover:bg-green-50'
                    ]"
                    :title="survey.is_active ? t('Деактивировать') : t('Активировать')"
                  >
                    <svg v-if="survey.is_active" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>

                  <button
                    @click="duplicateSurvey(survey.id)"
                    class="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                    title="Дублировать"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>

                  <button
                    @click="deleteSurvey(survey.id)"
                    class="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Удалить"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="overflow-hidden rounded-lg bg-white shadow">
            <StaffPagination :page="page" :page-size="pageSize" :total="total" @change="loadSurveys" @update:page-size="changePageSize" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import StaffSidebar from '@/components/staff/StaffSidebar.vue'
import StaffPagination from '@/components/staff/StaffPagination.vue'
import { t } from '@/i18n'

export default {
  name: 'StaffSurveys',

  components: {
    StaffSidebar,
    StaffPagination
  },

  data() {
    return {
      surveys: [],
      page: 1,
      pageSize: 20,
      total: 0,
      summary: null,
      loading: false,
      error: null,
      searchQuery: '',
      filterStatus: '',
      filterLanguage: ''
    }
  },

  computed: {
    stats() {
      return {
        total: this.summary?.total ?? this.total,
        active: this.summary?.active ?? this.surveys.filter(s => s.is_active).length,
        draft: this.summary?.draft ?? this.surveys.filter(s => !s.is_active).length,
        totalResponses: this.summary?.total_responses ?? this.surveys.reduce((sum, s) => sum + (s.response_count || 0), 0)
      }
    },

    filteredSurveys() {
      return this.surveys
    }
  },

  mounted() {
    this.loadSurveys()
  },

  methods: {
    t,

    async loadSurveys(page = this.page) {
      this.loading = true
      this.error = null

      try {
        const response = await api.getSurveys({
          page,
          page_size: this.pageSize,
          search: this.searchQuery || undefined,
          is_active: this.filterStatus ? this.filterStatus === 'active' : undefined,
          language: this.filterLanguage || undefined,
          ordering: '-created_at'
        })
        this.surveys = response.data?.results || response.data || []
        this.total = response.data?.count ?? this.surveys.length
        this.summary = response.data?.summary || null
        this.page = page
      } catch (err) {
        console.error('Error loading surveys:', err)
        this.error = t('Ошибка загрузки опросов. Проверьте подключение к серверу.')
      } finally {
        this.loading = false
      }
    },

    queueReload() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.loadSurveys(1), 300)
    },

    changePageSize(pageSize) {
      this.pageSize = pageSize
      this.loadSurveys(1)
    },

    createNewSurvey() {
      this.$router.push('/staff/surveys/create')
    },

    editSurvey(surveyId) {
      this.$router.push(`/staff/surveys/${surveyId}/edit`)
    },

    viewStatistics(surveyId) {
      this.$router.push(`/staff/surveys/${surveyId}/statistics`)
    },

    async toggleActive(survey) {
      try {
        if (survey.is_active) {
          await api.unpublishSurvey(survey.id)
        } else {
          await api.publishSurvey(survey.id)
        }
        await this.loadSurveys(this.page)
      } catch (err) {
        console.error('Error toggling survey status:', err)
        alert(t('Ошибка изменения статуса опроса'))
      }
    },

    async duplicateSurvey(surveyId) {
      try {
        await api.duplicateSurvey(surveyId)
        await this.loadSurveys(this.page)
      } catch (err) {
        console.error('Error duplicating survey:', err)
        alert(t('Ошибка дублирования опроса'))
      }
    },

    async deleteSurvey(surveyId) {
      if (!confirm(t('Вы уверены, что хотите удалить этот опрос? Все ответы также будут удалены.'))) {
        return
      }

      try {
        await api.deleteSurvey(surveyId)
        await this.loadSurveys(this.surveys.length === 1 && this.page > 1 ? this.page - 1 : this.page)
      } catch (err) {
        console.error('Error deleting survey:', err)
        alert(t('Ошибка удаления опроса'))
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
